// What the PostHog plugin scrubs and checks before anything reaches PostHog. Pure — no Nuxt.

// Query values that must never reach analytics: sign-in links carry a login token and the email.
export const SENSITIVE_QUERY_PARAMS = ['token', 'email', 'code', 'otp', 'password'] as const;

const SENSITIVE_PARAM = new RegExp(`([?&](?:${SENSITIVE_QUERY_PARAMS.join('|')})=)[^&#"'\\s]*`, 'gi');

/** "/login/auto?email=a@b.c&token=eyJ…" → "/login/auto?email=[redacted]&token=[redacted]". */
export const redactSensitiveParams = (value: string): string => value.replace(SENSITIVE_PARAM, '$1[redacted]');

type Props = Record<string, unknown>;

const redactProps = <T extends Props | undefined>(props: T): T => {
  if (!props) return props;
  const out: Props = {};
  for (const [key, value] of Object.entries(props)) {
    out[key] = typeof value === 'string' ? redactSensitiveParams(value) : value;
  }
  return out as T;
};

type Capture = { properties: Props; $set?: Props; $set_once?: Props };

/**
 * Every string an event carries — the page URL, the referrer, an autocaptured link's href in the
 * elements chain, the person's initial URL — with sensitive query values redacted.
 */
export const redactCapture = <T extends Capture>(capture: T): T => ({
  ...capture,
  properties: redactProps(capture.properties),
  $set: redactProps(capture.$set),
  $set_once: redactProps(capture.$set_once),
});

/**
 * True when the browser is still identified as someone else: switching accounts without signing
 * out (an auto-login link, an admin opening an agent's link) must start a new PostHog person,
 * or the new account's activity is filed under the previous one.
 */
export const isOtherIdentifiedUser = (userId: string, distinctId: string, userState: unknown): boolean =>
  userState === 'identified' && distinctId !== userId;
